import {
	apiServices,
	createUserVisibleError,
	getApiErrorMessage,
} from "../../../../core/utilities/ApiServices";
import {
	syncLocationStateFromUser,
} from "../../../../core/utilities/locationStorage";
import Category from "../../domain/entities/category";
import GetProfileConfigInfo from "../../domain/entities/getProfileConfigInfo";
import User from "../../domain/entities/getUser";
import ProfileConfig from "../../domain/entities/profileConfig";
import { ReferralCode } from "../../domain/entities/referralCode";
import UserInfo from "../../domain/entities/user";
import { UserTicket } from "../../domain/entities/userTicket";
import {
	SignupPhotoLivenessCheckResult,
	SignupPhotoLivenessSaveResult,
	SignupPhotoLivenessSession,
} from "../../domain/entities/signupPhotoLiveness";

export interface OnboardingDataSource {
	getUser(): Promise<User>;
	updateUser(data: UserInfo): Promise<User>;
	getCategories(endpoint: string): Promise<Category[]>;
	sendProfileConfig(data: ProfileConfig): Promise<GetProfileConfigInfo>;
	getProfileConfig(): Promise<GetProfileConfigInfo>;
	sendNewTicket(data: UserTicket): Promise<void>;
	getReferralCode(): Promise<ReferralCode>;
	checkSignupPhotoLiveness(params: {
		file: File;
		maritalStatusId?: string;
	}): Promise<SignupPhotoLivenessCheckResult>;
	saveSignupPhotoLivenessResult(params: {
		sessionId: string;
		contextToken: string;
	}): Promise<SignupPhotoLivenessSaveResult>;
}

export class OnboardingDataSourceImpl implements OnboardingDataSource {
	async getUser(): Promise<User> {
		const response = await apiServices.get(`/auth/info`);
		if (response.status === 200) {
			syncLocationStateFromUser(response.data);
			return response.data;
		} else {
			throw new Error();
		}
	}

	async updateUser(data: UserInfo): Promise<User> {
		const formData = new FormData();
		if (data.username) formData.append("username", data.username);
		if (data.birth) formData.append("birth", data.birth);
		if (data.gender_id) formData.append("gender_id", data.gender_id);
		if (data.token_push_notification) formData.append("token_push_notification", data.token_push_notification);
		if (data.sexual_orientation_id)
			formData.append("sexual_orientation_id", data.sexual_orientation_id);
		if (data.marital_status_id)
			formData.append("marital_status_id", data.marital_status_id);
		if (data.bio || data.bio === "")
			formData.append("bio", data.bio ?? "");
		if (data.status_onboarding !== undefined) {
			formData.append("status_onboarding", String(data.status_onboarding));
		}
		if (data.lat) formData.append("lat", data.lat);
		if (data.lng) formData.append("lng", data.lng);
		if (data.lat && data.lng && data.profile_location_source) {
			formData.append("profile_location_source", data.profile_location_source);
		}
		if (data.country_lat) formData.append("country_lat", data.country_lat);
		if (data.country_lng) formData.append("country_lng", data.country_lng);
		if (data.discovery_override_lat !== undefined)
			formData.append("discovery_override_lat", data.discovery_override_lat ?? "");
		if (data.discovery_override_lng !== undefined)
			formData.append("discovery_override_lng", data.discovery_override_lng ?? "");
		if (data.discovery_override_label !== undefined)
			formData.append("discovery_override_label", data.discovery_override_label ?? "");
		if (data.photosToPrivate) {
			data.photosToPrivate.forEach((photoId) =>
				formData.append("photosToPrivate", photoId)
			);
		}
		if (data.filesToRemove) {
			data.filesToRemove.forEach((fileId) =>
				formData.append("filesToRemove", fileId)
			);
		}
		if (data.photosToPublic) {
			data.photosToPublic.forEach((photoId) =>
				formData.append("photosToPublic", photoId)
			);
		}
		if (data.setAsProfile) formData.append("setAsProfile", data.setAsProfile);
		if (typeof data.show_last_logged === 'boolean') formData.append("show_last_logged", data.show_last_logged.toString());
		if (typeof data.matches === 'boolean') formData.append("matches", data.matches.toString());
		if (typeof data.chat === 'boolean') formData.append("chat", data.chat.toString());
		if (typeof data.likes === 'boolean') formData.append("likes", data.likes.toString());
		if (data.referral_code) formData.append("referral_code", data.referral_code);
		if (data.signup_photo_liveness_token) {
			formData.append("signup_photo_liveness_token", data.signup_photo_liveness_token);
		}

		data.files?.forEach((file, index) => {
			formData.append(`files`, file);
		});

		let response;
		try {
			response = await apiServices.put(`/auth/update`, formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});
		} catch (error) {
			throw createUserVisibleError(getApiErrorMessage(error));
		}

		if (response.status === 200) {
			syncLocationStateFromUser(response.data);
			return response.data;
		} else {
			throw new Error();
		}
	}

	async getCategories(endpoint: string): Promise<Category[]> {
		const response = await apiServices.get(`/profile/${endpoint}`);
		if (response.status === 200) {
			return response.data;
		} else {
			throw new Error();
		}
	}

	async sendProfileConfig(data: ProfileConfig): Promise<GetProfileConfigInfo> {
		const response = await apiServices.post(`/profile/config`, data, {
			headers: {
				"Content-Type": "application/json",
			},
		});
		if (response.status === 200) {
			return response.data;
		} else {
			throw new Error();
		}
	}

	async getProfileConfig(): Promise<GetProfileConfigInfo> {
		const response = await apiServices.get(`/profile/config`);
		if (response.status === 200) {
			return response.data;
		} else {
			throw new Error();
		}
	}
	async sendNewTicket(data: UserTicket): Promise<void> {
		const formData = new FormData();
		formData.append("title", data.title);
		formData.append("issue", data.issue);
		formData.append("type", data.type);
	
		if (data.reported_user_id) formData.append("reported_user_id", data.reported_user_id);
		if (data.report_subject_id) formData.append("report_subject_id", data.report_subject_id);
		if (data.reported_image_url) formData.append("reported_image_url", data.reported_image_url);
		if (data.file) formData.append("file", data.file);
	
		const response = await apiServices.post(`/profile/user-ticket`, formData, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});
	
		if (response.status === 200) {
			return;
		} else {
			throw new Error();
		}
	}

	async getReferralCode(): Promise<ReferralCode> {
		const response = await apiServices.get(`/profile/referral-code`);
		if (response.status === 200) {
			return response.data;
		} else {
			throw new Error();
		}
	}

	async checkSignupPhotoLiveness(params: {
		file: File;
		maritalStatusId?: string;
	}): Promise<SignupPhotoLivenessCheckResult> {
		const formData = new FormData();
		formData.append("file", params.file);
		if (params.maritalStatusId?.trim()) {
			formData.append("marital_status_id", params.maritalStatusId.trim());
		}

		let response;
		try {
			response = await apiServices.post(
				`/auth/onboarding-photo/liveness-check`,
				formData,
				{
					headers: {
						"Content-Type": "multipart/form-data",
					},
				}
			);
		} catch (error: any) {
			const status = error?.response?.status;

			// Old live backends do not have this route yet. Fall back to the pre-liveness flow.
			if (status === 404) {
				return { requiresLiveness: false };
			}

			throw createUserVisibleError(getApiErrorMessage(error));
		}

		if (response.status === 200 || response.status === 202) {
			return this.normalizeSignupPhotoLivenessCheckResult(response.data);
		}

		throw new Error("Error checking the signup photo for live face");
	}

	async saveSignupPhotoLivenessResult(params: {
		sessionId: string;
		contextToken: string;
	}): Promise<SignupPhotoLivenessSaveResult> {
		const response = await apiServices.post(`/auth/onboarding-photo/liveness-result`, {
			session_id: params.sessionId,
			context_token: params.contextToken,
		});

		if (response.status === 200) {
			const data = response.data || {};
			const status = typeof data.status === "string" ? data.status.trim().toLowerCase() : "";
			const approvalToken =
				typeof data.approval_token === "string" && data.approval_token.trim()
					? data.approval_token.trim()
					: null;
			if (status) {
				return { status, approvalToken };
			}
		}

		throw new Error("Could not save the signup live check result.");
	}

	private normalizeSignupPhotoLivenessCheckResult(data: any): SignupPhotoLivenessCheckResult {
		if (!data || typeof data !== "object") {
			return { requiresLiveness: false };
		}

		const session = this.normalizeSignupPhotoLivenessSession(data.liveness_session);
		const reason =
			typeof data.reason === "string" && data.reason.trim()
				? data.reason.trim()
				: typeof data.message === "string" && data.message.trim()
				? data.message.trim()
				: undefined;
		const contextToken =
			typeof data.context_token === "string" && data.context_token.trim()
				? data.context_token.trim()
				: undefined;

		return {
			requiresLiveness: data.requires_liveness === true,
			shouldBlock: data.should_block === true,
			reason,
			message:
				typeof data.message === "string" && data.message.trim()
					? data.message.trim()
					: undefined,
			contextToken,
			livenessSession: session,
		};
	}

	private normalizeSignupPhotoLivenessSession(
		session: any
	): SignupPhotoLivenessSession | undefined {
		if (!session || typeof session !== "object") {
			return undefined;
		}

		const region =
			typeof session.region === "string" && session.region.trim()
				? session.region.trim()
				: undefined;
		const sessionId =
			typeof session?.session_id === "string" && session.session_id.trim()
				? session.session_id.trim()
				: undefined;
		const accessKeyId =
			typeof session?.access_key_id === "string" &&
			session.access_key_id.trim()
				? session.access_key_id.trim()
				: undefined;
		const secretAccessKey =
			typeof session?.secret_access_key === "string" &&
			session.secret_access_key.trim()
				? session.secret_access_key.trim()
				: undefined;
		const sessionToken =
			typeof session?.session_token === "string" &&
			session.session_token.trim()
				? session.session_token.trim()
				: undefined;

		if (
			!sessionId ||
			!region ||
			!accessKeyId ||
			!secretAccessKey ||
			!sessionToken
		) {
			return undefined;
		}

		return {
			sessionId,
			region,
			accessKeyId,
			secretAccessKey,
			sessionToken,
			expiresAt:
				typeof session?.expires_at === "string"
					? session.expires_at
					: null,
		};
	}
}
